"use client";

import type React from "react";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";

// Define types for our context
interface AppContextType {
  theme: string;
  setTheme: (theme: string) => void;
  plans: Plan[];
  workers: Worker[];
  companies: Company[];
  sites: Site[];
  attendance: AttendanceRecord[];
  supportTickets: SupportTicket[];
  setAttendance: React.Dispatch<React.SetStateAction<AttendanceRecord[]>>;
  addWorker: (
    worker: Worker,
  ) => Promise<{ success: boolean; message: string; worker?: Worker }>;
  addSite: (
    site: Site,
  ) => Promise<{ success: boolean; message: string; site?: Site }>;
  addAttendanceRecord: (record: AttendanceRecord) => void;
  addSupportTicket: (ticket: SupportTicket) => void;
  addCompany: (company: Company) => void;
  updatePlan: (
    planId: string,
    planData: Partial<Plan>,
    toast?: (args: {
      title: string;
      description: string;
      variant?: string;
    }) => void,
  ) => void;
  createPlan: (
    planData: Partial<Plan>,
    toast?: (args: {
      title: string;
      description: string;
      variant?: string;
    }) => void,
  ) => void;
  deletePlan: (
    planId: string,
    toast?: (args: {
      title: string;
      description: string;
      variant?: string;
    }) => void,
  ) => void;
  currentUser: User | null;
  setCurrentUser: (user: User | null) => void;
  login: (email: string, password: string) => Promise<User | null>;
  logout: () => void;
  registerUser: (userData: UserRegistration) => Promise<User | null>;
  startFreeTrial: (companyData: CompanyRegistration) => Promise<boolean>;
  getCurrentCompany: () => Company | null;
  companyProfile: Company | null;
  setCompanyProfile: React.Dispatch<React.SetStateAction<Company | null>>;
  fetchCompanyProfile: (companyId: string) => Promise<void>;
  getWorkerCount: (companyId: string) => number;
  getWorkerLimit: (companyId: string) => number;
  setCompanies: React.Dispatch<React.SetStateAction<Company[]>>;
  setWorkers: React.Dispatch<React.SetStateAction<Worker[]>>;
  fetchWorkers: () => Promise<void>;
  deleteWorker: (
    workerId: string | number,
  ) => Promise<{ success: boolean; message: string }>;
  checkInWorker: (
    workerId: string,
    siteId: string,
    siteLoc: { lat: number; lng: number },
    currentLoc: { lat: number; lng: number },
    token?: string,
  ) => Promise<any>;
  checkOutWorker: (
    workerId: string,
    siteId: string,
    siteLoc: { lat: number; lng: number },
    currentLoc: { lat: number; lng: number },
    token?: string,
  ) => Promise<any>;
  fetchAttendance: () => Promise<void>;
}

//const API_BASE = process.env.NEXT_PUBLIC_API_URL || "https://checkinoutpro.com";
const API_BASE = process.env.NEXT_PUBLIC_API_URL || "http://localhost:5000";

// Define types for our data
interface Plan {
  id: number;
  _id?: string;
  title: string;
  description: string;
  monthlyPrice: number;
  annualPrice: number;
  features: string[];
  isPopular: boolean;
  workerLimit: number;
  order?: number;
}

export interface Worker {
  id?: number;
  _id?: string;
  employeeNumber: string;
  fullName: string;
  phone: string;
  email?: string;
  hasFingerprint: boolean;
  hasPhoto: boolean;
  photoData?: string;
  fingerprintData?: string;
  companyId: string;
  currentProject?: string;
  recentActivity?: any[];
  position?: string;
  active?: boolean;
}

interface Company {
  email: ReactNode;
  id: number;
  _id?: string;
  companyId: string;
  name: string;
  plan: string;
  users: number;
  status: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  country: string;
  city: string;
  joiningDate: string;
  expiryDate: string;
  subscriptionType: "monthly" | "annual";
  currentWorkerCount: number;
  password: string;
}

export interface Site {
  id?: string | number;
  name: string;
  projectNumber: string;
  location: string;
  latitude: number;
  longitude: number;
  startTime: string;
  endTime: string;
  radius: number;
}

interface AttendanceRecord {
  id?: number;
  employeeId: string;
  employeeName: string;
  checkInTime: string;
  checkInDate: string;
  checkInLocation: string;
  checkOutTime: string;
  checkOutDate: string;
  checkOutLocation: string;
  status: string;
  companyId?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface SupportTicket {
  id: number;
  subject: string;
  status: string;
  createdAt: string;
  messages: {
    id: number;
    sender: string;
    text: string;
    time: string;
  }[];
  companyId?: string;
  email?: string;
}

interface User {
  id: string;
  email: string;
  name: string;
  role: "admin" | "company" | "worker";
  companyId?: string;
  username?: string;
  fullName?: string;
  phone?: string;
}

interface UserRegistration {
  email: string;
  password: string;
  name: string;
  role: "admin" | "company" | "worker";
}

interface CompanyRegistration {
  name: string;
  email: string;
  password: string;
  plan: string;
  subscriptionType: "monthly" | "annual";
  contactName: string;
  contactPhone: string;
  country: string;
  city: string;
}

// Create the context
const AppContext = createContext<AppContextType | undefined>(undefined);

// Create a provider component
export function AppProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState("light");
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [companyProfile, setCompanyProfile] = useState<Company | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);

  const [plans, setPlans] = useState<Plan[]>([]);

  // Fetch plans from backend
  const fetchPlans = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/plans`);
      if (response.ok) {
        const data = await response.json();
        setPlans(data);
      }
    } catch (error) {}
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Initial workers data
  const [workers, setWorkers] = useState<Worker[]>([]);

  // Initial sites data
  const [sites, setSites] = useState<Site[]>([
    {
      id: 1,
      name: "Downtown Office",
      projectNumber: "PRJ-001",
      location: "Main Street, New York",
      latitude: 40.7128,
      longitude: -74.006,
      startTime: "09:00",
      endTime: "17:00",
      radius: 100,
    },
    {
      id: 2,
      name: "Construction Site Alpha",
      projectNumber: "PRJ-002",
      location: "Broadway, New York",
      latitude: 40.7589,
      longitude: -73.9851,
      startTime: "08:00",
      endTime: "16:00",
      radius: 200,
    },
  ]);

  // Initial attendance records (sample)
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([
    {
      id: 1,
      employeeId: "EMP-001",
      employeeName: "John Smith",
      checkInTime: "08:45 AM",
      checkInDate: "2023-06-15",
      checkInLocation: "Downtown Office",
      checkOutTime: "05:15 PM",
      checkOutDate: "2023-06-15",
      checkOutLocation: "Downtown Office",
      status: "on-time",
      companyId: "1",
    },
    {
      id: 2,
      employeeId: "EMP-002",
      employeeName: "Jane Doe",
      checkInTime: "09:15 AM",
      checkInDate: "2023-06-15",
      checkInLocation: "Construction Site Alpha",
      checkOutTime: "04:45 PM",
      checkOutDate: "2023-06-15",
      checkOutLocation: "Construction Site Alpha",
      status: "late",
      companyId: "1",
    },
  ]);

  // Initial support tickets
  const [supportTickets, setSupportTickets] = useState<SupportTicket[]>([
    {
      id: 1,
      subject: "GPS tracking not working",
      status: "open",
      createdAt: "2023-06-15T10:30:00",
      companyId: "1",
      messages: [
        {
          id: 1,
          sender: "company",
          text: "Our GPS tracking has stopped working for all our field workers. Can you help?",
          time: "2023-06-15T10:30:00",
        },
        {
          id: 2,
          sender: "admin",
          text: "I'm sorry to hear that. Can you tell me when you first noticed the issue?",
          time: "2023-06-15T10:45:00",
        },
      ],
    },
    {
      id: 2,
      subject: "Billing question",
      status: "open",
      createdAt: "2023-06-14T14:20:00",
      companyId: "2",
      messages: [
        {
          id: 1,
          sender: "company",
          text: "We were charged twice this month. Can you check our billing?",
          time: "2023-06-14T14:20:00",
        },
      ],
    },
  ]);

  // Function to get current company
  const getCurrentCompany = () => {
    return companyProfile;
  };

  // Function to get worker count for a company
  const getWorkerCount = (companyId: string) => {
    return workers.filter((worker) => worker.companyId === companyId).length;
  };

  // Function to get worker limit for a company
  const getWorkerLimit = (companyId: string) => {
    const company = companies.find(
      (c) => c._id === companyId || c.id?.toString() === companyId,
    );
    if (!company) return 0;

    const plan = plans.find((p) => p.title === company.plan);
    return plan ? plan.workerLimit : 0;
  };

  // Function to add a worker
  const addWorker = async (
    worker: Worker,
  ): Promise<{ success: boolean; message: string; worker?: Worker }> => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/api/workers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(worker),
      });
      if (response.ok) {
        const data = await response.json();
        return {
          success: true,
          message: "Worker added successfully",
          worker: data.worker,
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          message: (errorData as any).message || "Failed to add worker",
        };
      }
    } catch (error) {
      return { success: false, message: "Failed to add worker" };
    }
  };

  // const fetchAttendance = async () => {
  //   try {
  //     const token =
  //       typeof window !== "undefined" ? localStorage.getItem("token") : null;
  //     const companyId = currentUser?.companyId;
  //     if (!companyId) return;

  //     const res = await fetch(
  //       `${API_BASE}/api/attendance?companyId=${encodeURIComponent(companyId)}`,
  //       {
  //         headers: {
  //           ...(token ? { Authorization: `Bearer ${token}` } : {}),
  //         },
  //       }
  //     );

  //     if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  //     const data = await res.json();

  //     // ✅ Handle { success: true, data: [...] }
  //     const records = Array.isArray(data)
  //       ? data
  //       : Array.isArray(data?.data)
  //       ? data.data
  //       : [];

  //     setAttendance(records);
  //   } catch (err) {}
  // };

  const fetchAttendance = useCallback(async () => {
    const cid = currentUser?.companyId;
    if (!cid) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/api/attendance?companyId=${cid}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (res.ok) {
        const data = await res.json();
        const records = Array.isArray(data)
          ? data
          : data.data || data.records || [];
        setAttendance(records);
      }
    } catch (error) {
      console.error("Fetch attendance error:", error);
    }
  }, [currentUser?.companyId]);

  // const fetchAttendance = async () => {
  //   const token = localStorage.getItem("token"); // Get fresh token
  //   if (!token) return;

  //   try {
  //     const res = await fetch(`${API_BASE}/api/attendance`, {
  //       headers: { Authorization: `Bearer ${token}` },
  //     });
  //     const data = await res.json();
  //     setAttendance(data.records || data); // Adjust based on your API response structure
  //   } catch (err) {
  //     console.error("Fetch attendance error:", err);
  //   }
  // };

  const fetchWorkers = useCallback(async () => {
    const cid = currentUser?.companyId;
    if (!cid) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/api/workers/company/${cid}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (res.ok) setWorkers(await res.json());
    } catch (error) {
      console.error("Fetch workers error:", error);
    }
  }, [currentUser?.companyId]);

  const createPlan = async (planData: any, toast?: Function) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/api/plans`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(planData),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast?.({
          title: "Error",
          description: data.message || "Failed to create plan",
          variant: "destructive",
        });
        console.error("Failed to create plan:", data);
        return null;
      }

      toast?.({
        title: "Plan Created",
        description: "The new plan was added successfully.",
        variant: "success",
      });

      //  Refresh plans list after success
      await fetchPlans();

      return data;
    } catch (error: any) {
      console.error("Error creating plan:", error);
      toast?.({
        title: "Error",
        description: error.message || "An unexpected error occurred.",
        variant: "destructive",
      });
      return null;
    }
  };

  // Update an existing plan
  const updatePlan = async (id: string, updates: any, toast?: Function) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/api/plans/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(updates),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast?.({
          title: "Failed to update plan",
          description: data.message || "Something went wrong.",
          variant: "destructive",
        });
        console.error("Failed to update plan:", data);
        return null;
      }

      toast?.({
        title: "Plan Updated",
        description: "Changes saved successfully.",
        variant: "success",
      });

      // 🔄 Refresh plans
      await fetchPlans();

      return data;
    } catch (error: any) {
      console.error("Error updating plan:", error);
      toast?.({
        title: "Error",
        description: error.message || "An unexpected error occurred.",
        variant: "destructive",
      });
      return null;
    }
  };

  // Delete a plan
  const deletePlan = async (id: string, toast?: Function) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/api/plans/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast?.({
          title: "Error Deleting Plan",
          description: data.message || "Failed to delete the plan.",
          variant: "destructive",
        });
        console.error(" Failed to delete plan:", data);
        return false;
      }

      toast?.({
        title: "Plan Deleted",
        description: "The plan was removed successfully.",
        variant: "success",
      });

      //  Refresh plans
      await fetchPlans();

      return true;
    } catch (error: any) {
      console.error("Error deleting plan:", error);
      toast?.({
        title: "Error",
        description: error.message || "An unexpected error occurred.",
        variant: "destructive",
      });
      return false;
    }
  };

  // Function to add a site
  const addSite = async (
    site: Site,
  ): Promise<{ success: boolean; message: string; site?: Site }> => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/api/sites`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(site),
      });
      if (response.ok) {
        const data = await response.json();
        setSites((prev) => [...prev, data]);
        return {
          success: true,
          message: "Site added successfully",
          site: data,
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          message: (errorData as any).message || "Failed to add site",
        };
      }
    } catch (error) {
      return { success: false, message: "Failed to add site" };
    }
  };

  // Function to add an attendance record (local-only helper)
  const addAttendanceRecord = (record: AttendanceRecord) => {
    setAttendance([...attendance, { ...record, id: attendance.length + 1 }]);
  };

  // Function to add a support ticket
  const addSupportTicket = (ticket: SupportTicket) => {
    setSupportTickets([...supportTickets, ticket]);
  };

  // Function to add a company
  const addCompany = (company: Company) => {
    setCompanies([...companies, company]);
  };

  // working login
  const login = async (
    email: string,
    password: string,
  ): Promise<User | null> => {
    try {
      const base = API_BASE.replace(/\/$/, "");
      //  Try MASTER ADMIN login first
      {
        const res = await fetch(`${base}/api/auth/signup`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "login",
            role: "admin",
            email,
            password,
          }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.token) localStorage.setItem("token", data.token);
          const u = data.user || data.admin || {};
          const user: User = {
            id: String(u.id || u._id || ""),
            email: u.email || u.username || email,
            name: u.username || u.email || "Admin",
            role: "admin",
          };
          setCurrentUser(user);
          return user;
        } else if (![401, 403, 404].includes(res.status)) {
          // unexpected errors (like 500)
          const errorText = await res.text();
          throw new Error(errorText || "Unexpected error during admin login.");
        }
      }
      const response = await fetch(`${base}/api/companies/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const msg =
          data?.message ||
          (response.status === 403
            ? "Access denied. Please contact support."
            : "Invalid email or password.");
        throw new Error(msg);
      }
      const isValidObjectId = /^[a-fA-F0-9]{24}$/.test(data.company.id);
      if (!isValidObjectId) {
        throw new Error(
          "Login failed: Invalid company ID returned from backend. Please contact support.",
        );
      }
      const user: User = {
        id: data.company.id,
        email: data.company.email,
        name: data.company.name,
        role: "company",
        companyId: data.company.id,
      };
      setCurrentUser(user);
      if (data.token) localStorage.setItem("token", data.token);
      return user;
    } catch (err: any) {
      console.error("Login error:", err);
      throw new Error(err.message || "Unable to log in. Please try again.");
    }
  };

  // Function to logout

  const logout = () => {
    try {
      localStorage.removeItem("token");
    } catch {}
    try {
      localStorage.removeItem("currentUser");
    } catch {}
    setCurrentUser(null);
  };

  // Function to register a new user (demo)
  const registerUser = async (
    userData: UserRegistration,
  ): Promise<User | null> => {
    await new Promise((resolve) => setTimeout(resolve, 1000));

    const newUser: User = {
      id: String(Math.floor(Math.random() * 1000) + 10),
      email: userData.email,
      name: userData.name,
      role: userData.role,
    };

    setCurrentUser(newUser);
    return newUser;
  };

  // Helper to generate a short 4-character alphanumeric companyId (unused)
  function generateShortCompanyId() {
    return Math.random().toString(36).substring(2, 6).toUpperCase();
  }

  // Function to start a free trial
  const startFreeTrial = async (
    companyData: CompanyRegistration,
  ): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE}/api/companies/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(companyData),
      });

      if (response.status === 201 || response.status === 200) {
        // Fetch the latest companies from the backend
        const companiesRes = await fetch(`${API_BASE}/api/companies`, {
          headers: { "Content-Type": "application/json" },
        });
        if (companiesRes.ok) {
          const companiesData = await companiesRes.json();
          setCompanies(companiesData);
        }
        return true;
      } else {
        let errorMessage = "Failed to create account";
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          // If response is not JSON, keep the default message
        }
        return false;
      }
    } catch (error) {
      return false;
    }
  };

  // const fetchCompanyProfile = useCallback(async (companyId: string) => {
  //   try {
  //     const token = localStorage.getItem("token");
  //     const response = await fetch(`${API_BASE}/api/companies/profile`, {
  //       headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  //     });

  //     const data = await response.json().catch(() => ({}));
  //     if (!response.ok) {
  //       setCompanyProfile(null);
  //     }

  //     const company = (data as any)?.company ?? data;
  //     if (!company || typeof company !== "object") {
  //       setCompanyProfile(null);
  //       return;
  //     }

  //     setCompanyProfile(company as Company);
  //     localStorage.setItem("currentCompany", JSON.stringify(company));
  //   } catch (error) {
  //     setCompanyProfile(null);
  //   }
  // }, []);

  const fetchCompanyProfile = useCallback(async (cid: string) => {
    if (!cid) return; // Guard clause
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/api/companies/profile`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (res.ok) {
        const data = await res.json();
        setCompanyProfile(data.company || data);
      }
    } catch (error) {
      console.error("Fetch profile error:", error);
    }
  }, []);

  useEffect(() => {
    const cid = currentUser?.companyId;
    if (cid) {
      const loadAllData = async () => {
        // Promise.all ensures speed and removes the "sequential wait"
        await Promise.all([
          fetchCompanyProfile(cid), // Here cid is guaranteed string
          fetchAttendance(),
          fetchWorkers(),
        ]);
      };
      loadAllData();
    }
  }, [
    currentUser?.id,
    currentUser?.companyId,
    fetchAttendance,
    fetchWorkers,
    fetchCompanyProfile,
  ]);

  useEffect(() => {}, [companyProfile]);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme) {
      setTheme(savedTheme);
    }
  }, []);

  // Save theme to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("theme", theme);

    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [theme]);

  // Restore currentUser from localStorage on app load
  useEffect(() => {
    const savedUser = localStorage.getItem("currentUser");

    if (savedUser) {
      const parsed = JSON.parse(savedUser);
      setCurrentUser(parsed);

      // ✅ Auto-fetch company profile only for company admins
      if (parsed.role === "company" && parsed.companyId) {
        fetchCompanyProfile(parsed.companyId);
      }
    } else {
      fetch(`${API_BASE}/api/companies`)
        .then((res) => res.json())
        .then((companies) => {
          if (Array.isArray(companies) && companies.length > 0) {
            const first = companies[0];
            if (first && first.email === "a@gmail.com") {
              setCurrentUser({
                id: String(first._id || first.id),
                email: first.email,
                name: first.name,
                role: "company",
                companyId: String(first._id || first.id),
              });
            }
          }
        });
    }
  }, []);

  // Save currentUser to localStorage whenever it changes
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem("currentUser", JSON.stringify(currentUser));
    } else {
      localStorage.removeItem("currentUser");
    }
  }, [currentUser]);

  // Fetch company profile after login or registration
  useEffect(() => {
    if (currentUser && currentUser.companyId) {
      fetchCompanyProfile(String(currentUser.companyId));
    }
  }, [currentUser, fetchCompanyProfile]);

  // Fetch workers for the current company when currentUser changes
  useEffect(() => {
    const fetchWorkers = async () => {
      if (currentUser && currentUser.companyId) {
        try {
          const token = localStorage.getItem("token");
          const workersRes = await fetch(
            `${API_BASE}/api/workers/company/${currentUser.companyId}`,
            {
              headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
            },
          );

          if (workersRes.ok) {
            const workersData = await workersRes.json();
            setWorkers(workersData);
          }
        } catch (error) {}
      }
    };
    fetchWorkers();
  }, [currentUser]);

  // Fetch sites for the current company when currentUser changes
  useEffect(() => {
    const fetchSites = async () => {
      if (currentUser && currentUser.companyId) {
        try {
          const token = localStorage.getItem("token");
          const sitesRes = await fetch(`${API_BASE}/api/sites`, {
            headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          });
          if (sitesRes.ok) {
            const sitesData = await sitesRes.json();
            setSites(sitesData);
          }
        } catch (error) {
          // Optionally handle error
        }
      }
    };
    fetchSites();
  }, [currentUser]);

  // Fetch attendance for the current company when currentUser changes (company-scoped)
  useEffect(() => {
    const fetchAttendance = async () => {
      if (!currentUser) return;
      const companyId = currentUser.companyId || currentUser.id; // fallback for admin
      if (!companyId) return;

      try {
        const token = localStorage.getItem("token");
        const attendanceRes = await fetch(
          `${API_BASE}/api/attendance?companyId=${encodeURIComponent(
            companyId,
          )}`,
          {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          },
        );

        if (attendanceRes.ok) {
          const data = await attendanceRes.json();
          // ✅ Reuse normalization logic
          let records: any[] = [];
          if (Array.isArray(data)) records = data;
          else if (Array.isArray(data?.data)) records = data.data;
          else if (Array.isArray(data?.records)) records = data.records;

          const normalized = records.map((r: any) => ({
            ...r,
            employeeId: r.employeeId || r.worker?._id || r.worker || "N/A",
            employeeName: r.employeeName || r.worker?.fullName || "Unknown",
          }));
          setAttendance(normalized);
        }
      } catch (error) {}
    };
    fetchAttendance();
  }, [currentUser]);

  //  Worker attendance actions
  const checkInWorker = async (
    workerId: string,
    siteId: string,
    siteLoc: { lat: number; lng: number },
    currentLoc: { lat: number; lng: number },
    token?: string,
  ) => {
    try {
      const authToken =
        token ??
        (typeof window !== "undefined"
          ? (localStorage.getItem("token") ?? undefined)
          : undefined);

      if (!authToken) throw new Error("No authorization token found");

      const payload = {
        worker: workerId,
        site: siteId,
        siteLocation: siteLoc,
        currentLocation: currentLoc,
        radiusMeters: 500,
      };

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000); // 15s timeout

      const res = await fetch(`${API_BASE}/api/attendance/check-in`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      clearTimeout(timeout);

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Check-in failed: ${res.status} ${errText}`);
      }

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        if (currentUser?.companyId) {
          const attRes = await fetch(
            `${API_BASE}/api/attendance?companyId=${encodeURIComponent(
              currentUser.companyId!,
            )}`,
            {
              headers: {
                ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
              },
            },
          );
          if (attRes.ok) {
            const attData = await attRes.json();
            setAttendance(attData);
          }
        }
      }
      // await fetchAttendance();
      return data;
    } catch (error: any) {
      return { success: false, message: error.message || "Check-in failed" };
    }
  };

  const checkOutWorker = async (
    workerId: string,
    siteId: string,
    siteLoc: { lat: number; lng: number },
    currentLoc: { lat: number; lng: number },
    token?: string,
  ) => {
    const authToken =
      token ??
      (typeof window !== "undefined"
        ? (localStorage.getItem("token") ?? undefined)
        : undefined);
    const payload = {
      worker: workerId,
      site: siteId,
      siteLocation: siteLoc,
      currentLocation: currentLoc,
      radiusMeters: 500,
    };
    const res = await fetch(`${API_BASE}/api/attendance/check-out`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    const data = await res.json().catch(() => ({}));

    if (res.ok && currentUser?.companyId) {
      const attRes = await fetch(
        `${API_BASE}/api/attendance?companyId=${encodeURIComponent(
          currentUser.companyId!,
        )}`,
        {
          headers: {
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          },
        },
      );
      if (attRes.ok) {
        const attData = await attRes.json();
        const normalized = Array.isArray(attData)
          ? attData
          : Array.isArray(attData?.data)
            ? attData.data
            : Array.isArray(attData?.records)
              ? attData.records
              : [];
        setAttendance(normalized);
      }
    }

    return data;
  };

  // Fetch support tickets for the current company when currentUser changes
  useEffect(() => {
    const fetchSupportTickets = async () => {
      if (currentUser && currentUser.companyId) {
        try {
          const token = localStorage.getItem("token");
          const ticketsRes = await fetch(
            `${API_BASE}/api/support/messages?userId=${currentUser.companyId}`,
            {
              headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
            },
          );
          if (ticketsRes.ok) {
            const ticketsData = await ticketsRes.json();
            setSupportTickets(ticketsData);
          }
        } catch (error) {
          // Optionally handle error
        }
      }
    };
    fetchSupportTickets();
  }, [currentUser]);

  // Function to delete a worker
  const deleteWorker = async (
    workerId: string | number,
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/api/workers/${workerId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (response.ok) {
        setWorkers((ws) =>
          ws.filter((w) => String(w.id ?? w._id) !== String(workerId)),
        );
        return { success: true, message: "Worker deleted successfully" };
      } else {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          message: (errorData as any).message || "Failed to delete worker",
        };
      }
    } catch (error) {
      return { success: false, message: "Failed to delete worker" };
    }
  };

  return (
    <AppContext.Provider
      value={{
        theme,
        setTheme,
        plans,
        workers,
        companies,
        sites,
        attendance,
        supportTickets,
        addWorker,
        addSite,
        addAttendanceRecord,
        setAttendance,
        addSupportTicket,
        addCompany,
        updatePlan,
        createPlan,
        deletePlan,
        currentUser,
        setCurrentUser,
        login,
        logout,
        registerUser,
        startFreeTrial,
        getCurrentCompany,
        getWorkerCount,
        getWorkerLimit,
        setCompanies,
        setWorkers,
        fetchWorkers,
        fetchCompanyProfile,
        companyProfile,
        setCompanyProfile,
        deleteWorker,
        checkInWorker,
        checkOutWorker,
        fetchAttendance,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

// Create a custom hook to use the context
export function useAppContext() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
}
